// สัญญา localStorage ระหว่างแอปกับหน้า static ใน public/seat/
//
// หน้า static ไม่มี firebase / bundle ของแอป (ตั้งใจ: ไปหน้านั้นแล้ว listener ของโหมดเดิม
// ตายหมดแน่นอน) จึงคุยกับแอปผ่าน localStorage อย่างเดียว:
//
//   seatChangeRequest  หน้าเว็บ (static / select-run-mode / ปุ่มเปลี่ยนโหมด) → plugin seatRequest
//   seatConflict       plugin seatMain / seatSecond → หน้า ask-*-override
//   seatLost           plugin seatMain / seatSecond → หน้า *-is-taken
//
// public/seat/seat.js ใช้ key ชุดเดียวกันนี้ — แก้ที่นี่ต้องแก้ที่นั่นด้วย
// ไฟล์นี้ห้าม import firebase / pinia / nuxt (scripts/test-cashier-seat.mjs transpile ไปรันใน node)

import type { SeatHolder, SeatKick, SeatMode } from "./seatRules";

export const SEAT_REQUEST_KEY = "seatChangeRequest";
export const SEAT_CONFLICT_KEY = "seatConflict";
export const SEAT_LOST_KEY = "seatLost";

/** ตั้งโดย seatBlock เมื่อเครื่องนี้โดนบล็อก (ชื่อ key เดิมจาก deviceBlock ห้ามเปลี่ยน) */
export const DEVICE_BLOCKED_KEY = "deviceBlocked";
/** หน้า blocked เขียนตอนร้านกด "เปลี่ยนโหมด" (มีเฉพาะเมื่อ support ตั้ง allowChangeMode) */
export const PENDING_SELF_UNBLOCK_KEY = "pendingSelfUnblock";
/** หน้า blocked เขียนตอน IT support ใส่รหัสลับถูก — seatBlock ลบ node blocked ให้ตอน boot */
export const SUPPORT_UNBLOCK_KEY = "supportUnblock";

export const SEAT_PAGES = {
  askMain: "/seat/ask-main-cashier-override.html",
  askSecond: "/seat/ask-2nd-cashier-override.html",
  mainTaken: "/seat/main-cashier-is-taken.html",
  secondTaken: "/seat/2nd-cashier-is-taken.html",
  blocked: "/seat/blocked.html",
} as const;

/** request ที่ค้างนานกว่านี้ทิ้ง (เช่นกดไว้แล้วปิดเครื่องข้ามวัน) */
export const SEAT_REQUEST_MAX_AGE_MS = 10 * 60 * 1000;

export interface SeatChangeRequest {
  /** โหมดใหม่ (ค่าใน RunMode) */
  mode: string;
  /** Main: ยืนยันแย่งที่นั่งจากเครื่องที่ถืออยู่ */
  takeOver?: boolean;
  /** Second: Computer ID ของเครื่องที่จะแย่งที่นั่งมา */
  kick?: string | null;
  storeId: string | null;
  at: string;
}

export interface SeatConflict {
  mode: SeatMode;
  reason: "main_taken" | "second_full" | "no_quota";
  holders: SeatHolder[];
  used: number;
  quota: number;
  storeId: string;
  /** ชื่อร้าน (stores/{storeId}/_name) — อ่านไม่ได้ = "" */
  storeName: string;
  computerId: string;
  at: string;
}

export interface SeatLost {
  mode: SeatMode;
  /** ใครเข้ามาแทน (null = ไม่มีบันทึก เช่นโดนตัดเพราะหลุดนาน) */
  by: SeatKick | null;
  /** เครื่องที่ถือที่นั่งอยู่ตอนนี้ */
  holders: SeatHolder[];
  storeId: string;
  /** ชื่อร้าน (stores/{storeId}/_name) — อ่านไม่ได้ = "" */
  storeName: string;
  computerId: string;
  at: string;
}

/**
 * ตรวจ request (ฟังก์ชันล้วน ใช้ทั้ง plugin และเทสต์)
 * - JSON เสีย / mode ไม่รู้จัก / เก่าเกิน 10 นาที / ฝากไว้ตอนอยู่ร้านอื่น → null
 */
export function parseSeatRequest(
  raw: string | null,
  opts: { storeId: string; now: number; validModes: readonly string[] }
): SeatChangeRequest | null {
  if (!raw) return null;
  let value: any;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  if (typeof value.mode !== "string" || !opts.validModes.includes(value.mode)) return null;

  const at = Date.parse(value.at);
  if (!Number.isFinite(at) || opts.now - at > SEAT_REQUEST_MAX_AGE_MS || at - opts.now > 60 * 1000) {
    return null;
  }
  if (value.storeId && opts.storeId && value.storeId !== opts.storeId) return null;

  return {
    mode: value.mode,
    takeOver: value.takeOver === true,
    kick: typeof value.kick === "string" && value.kick ? value.kick : null,
    storeId: value.storeId || null,
    at: value.at,
  };
}

/**
 * เปลี่ยนโหมดจากหน้าในแอป — ฝาก request แล้วโหลด "/" ใหม่ทั้งหน้า
 * plugin seatRequest จัดการต่อตอน boot (คืน / จอง / แย่งที่นั่ง แล้วตั้งโหมด)
 */
export function requestRunMode(
  mode: string,
  extra: { takeOver?: boolean; kick?: string | null; storeId?: string | null } = {}
): void {
  writeJson(SEAT_REQUEST_KEY, {
    mode,
    takeOver: extra.takeOver === true,
    kick: extra.kick ?? null,
    storeId: extra.storeId ?? null,
    at: new Date().toISOString(),
  });
  window.location.replace("/");
}

export function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // private mode / quota เต็ม — หน้าปลายทางโชว์ข้อความกลาง ๆ แทน
  }
}

export function takeRaw(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    localStorage.removeItem(key);
    return raw;
  } catch {
    return null;
  }
}

export function hasKey(key: string): boolean {
  try {
    return localStorage.getItem(key) != null;
  } catch {
    return false;
  }
}

export function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}
